import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { Separator } from "@/components/ui/separator";
import {
	Mail,
	Upload,
	Send,
	X,
	AlertCircle,
	CheckCircle2,
	FileText,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import type { InvitationPayload } from "@/lib/invitation/invitation.types";
import { OrgaAdminService } from "@/lib/orgaAdmin/orgaAdmin.service";
import { useAuth } from "@/lib/contexts/auth.context";

type Person = {
	id: string;
	name: string;
	gruppe?: "Nr. 1" | "Nr. 2" | "Nr. 3";
	tag?: "Tag 1" | "Tag 2" | "Tag 3";
	position?: "1st" | "2t" | "3tt";
	email?: string;
};

interface EmailInvitationProps {
	onSingleEmailInvite: (email: string) => void;
	onBulkEmailInvite: (people: Person[]) => void;
	onClose: () => void;
}

interface ParsedEmail {
	email: string;
	isValid: boolean;
	row: number;
}

/**
 * This component is used to invite people to an organization.
 * It can be used to invite a single person or a bulk of people.
 * It can also be used to reset the bulk upload.
 *
 * @param onSingleEmailInvite - The function to invite a single person
 */
export default function EmailInvitation({
	onSingleEmailInvite,
}: EmailInvitationProps) {
	const { currentOrganization } = useAuth();
	// Single email state
	const [singleEmail, setSingleEmail] = useState("");
	const [emailError, setEmailError] = useState<string | null>(null);

	// Bulk upload state
	const [file, setFile] = useState<File | null>(null);
	const [parsedEmails, setParsedEmails] = useState<ParsedEmail[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [bulkError, setBulkError] = useState<string | null>(null);
	const [isDragOver, setIsDragOver] = useState(false);

	// Email validation
	const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
	const validateEmail = (email: string): boolean =>
		emailRegex.test(email.trim());

	// Single email handlers
	const handleSingleEmailChange = (value: string) => {
		setSingleEmail(value);
		if (value && !validateEmail(value)) {
			setEmailError("Bitte geben Sie eine gültige E-Mail-Adresse ein");
		} else {
			setEmailError(null);
		}
	};

	/**
	 * Handles the submission of the single email input.
	 * It will validate the email and call the onSingleEmailInvite function.
	 */
	const handleSingleEmailSubmit = () => {
		if (!singleEmail) {
			setEmailError("E-Mail-Adresse ist erforderlich");
			return;
		}
		if (!validateEmail(singleEmail)) {
			setEmailError("Bitte geben Sie eine gültige E-Mail-Adresse ein");
			return;
		}
		onSingleEmailInvite(singleEmail);
		setSingleEmail("");
		setEmailError(null);
	};

	// CSV parsing
	const parseCSV = (csvText: string): ParsedEmail[] => {
		const lines = csvText.split("\n").filter((line) => line.trim() !== "");
		const emails: ParsedEmail[] = [];

		lines.forEach((line, index) => {
			const columns = line.split(",");
			const emailCandidate = columns[0]?.trim().replace(/"/g, "");

			if (emailCandidate) {
				emails.push({
					email: emailCandidate,
					isValid: validateEmail(emailCandidate),
					row: index + 1,
				});
			}
		});

		return emails;
	};

	// File handlers
	const validateAndSetFile = (selectedFile: File) => {
		if (
			selectedFile.type !== "text/csv" &&
			!selectedFile.name.endsWith(".csv")
		) {
			setBulkError("Bitte wählen Sie eine CSV-Datei aus.");
			return false;
		}
		setFile(selectedFile);
		setBulkError(null);
		setParsedEmails([]);
		return true;
	};

	const handleFileChange = async (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		const selectedFile = event.target.files?.[0];
		if (selectedFile && validateAndSetFile(selectedFile)) {
			await analyzeFile(selectedFile);
		}
	};

	const analyzeFile = async (fileToAnalyze: File) => {
		setIsLoading(true);
		setBulkError(null);

		try {
			const text = await fileToAnalyze.text();
			const emails = parseCSV(text);
			setParsedEmails(emails);
		} catch (err) {
			setBulkError(
				"Fehler beim Lesen der Datei. Stellen Sie sicher, dass es sich um eine gültige CSV-Datei handelt.",
			);
		} finally {
			setIsLoading(false);
		}
	};

	// Drag and drop handlers
	const handleDragEnter = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragOver(true);
	};

	const handleDragLeave = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragOver(false);
	};

	const handleDragOver = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
	};

	const handleDrop = async (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragOver(false);

		const files = e.dataTransfer.files;
		if (files.length > 0) {
			const droppedFile = files[0];
			if (validateAndSetFile(droppedFile)) {
				await analyzeFile(droppedFile);
			}
		}
	};

	/**
	 * Handles the submission of the bulk import.
	 * It will validate the emails and call the onBulkEmailInvite function.
	 */
	const handleBulkImport = () => {
		const validEmails = parsedEmails.filter((email) => email.isValid);

		if (validEmails.length === 0) {
			setBulkError("Keine gültigen E-Mail-Adressen gefunden.");
			return;
		}

		const invitations: InvitationPayload[] = validEmails.map(
			(emailData, _) => ({
				email: emailData.email,
			}),
		);

		// here we do the api call to invite the people
		// TODO: Implement this
		console.log("Inviting people:", invitations);
		if (!currentOrganization) {
			throw new Error("No organization found");
		}

		OrgaAdminService.getInstance().bulkInviteUsersToOrganization(
			currentOrganization.organizationId,
			invitations,
		);
	};

	const clearBulkData = () => {
		setFile(null);
		setParsedEmails([]);
		setBulkError(null);
	};

	const validEmailsCount = parsedEmails.filter((email) => email.isValid).length;
	const invalidEmailsCount = parsedEmails.filter(
		(email) => !email.isValid,
	).length;
	const hasData = parsedEmails.length > 0;

	// Determine which sections to show
	const showSingleEmail = !hasData; // Show if no CSV data
	const showCSV = !singleEmail; // Show if no email input
	const showSeparator = showSingleEmail && showCSV; // Show separator only if both sections are visible

	return (
		<div className="space-y-6 p-6 max-h-[calc(100vh-8rem)] overflow-y-auto">
			{/* Single Email Input Section */}
			<div
				className={`transition-all duration-300 ease-in-out ${
					showSingleEmail
						? "opacity-100 max-h-96 translate-y-0"
						: "opacity-0 max-h-0 -translate-y-2 overflow-hidden"
				}`}
			>
				<Card className="">
					<CardContent className="p-6">
						<div className="flex items-center justify-between mb-4">
							<div className="flex items-center gap-3">
								<div className="p-2 rounded-lg bg-blue-50">
									<Mail className="h-5 w-5 text-blue-600" />
								</div>
								<div>
									<h3 className="text-lg font-semibold text-slate-900">
										Email einladen
									</h3>
									<p className="text-sm text-slate-600">
										Einzelne Person per E-Mail einladen
									</p>
								</div>
							</div>
							{hasData && (
								<Button
									variant="ghost"
									size="sm"
									onClick={clearBulkData}
									className="text-slate-500 hover:text-slate-700"
								>
									<X className="h-4 w-4 mr-1" />
									CSV zurücksetzen
								</Button>
							)}
						</div>

						<div className="space-y-4">
							<div className="space-y-2">
								<Label htmlFor="email" className="text-sm font-medium">
									E-Mail-Adresse *
								</Label>
								<Input
									id="email"
									type="email"
									placeholder="beispiel@domain.de"
									value={singleEmail}
									onChange={(e) => handleSingleEmailChange(e.target.value)}
									className={`${emailError ? "border-red-300 focus:ring-red-200" : ""}`}
								/>
								{emailError && (
									<div className="flex items-center gap-2 text-sm text-red-600">
										<AlertCircle className="h-4 w-4" />
										{emailError}
									</div>
								)}
							</div>

							<div className="flex justify-end gap-3">
								<Button
									onClick={handleSingleEmailSubmit}
									disabled={!singleEmail || !!emailError}
									className="flex items-center gap-2"
								>
									<Send className="h-4 w-4" />
									Einladung senden
								</Button>
							</div>
						</div>
					</CardContent>
				</Card>
			</div>

			{/* Separator */}
			<div
				className={`transition-all duration-300 ease-in-out ${
					showSeparator
						? "opacity-100 max-h-8 translate-y-0"
						: "opacity-0 max-h-0 -translate-y-1 overflow-hidden"
				}`}
			>
				<div className="flex items-center gap-4">
					<Separator className="flex-1" />
					<span className="text-sm text-slate-500 font-medium">ODER</span>
					<Separator className="flex-1" />
				</div>
			</div>

			{/* CSV Upload Section */}
			<div
				className={`transition-all duration-300 ease-in-out ${
					showCSV
						? "opacity-100 max-h-[600px] translate-y-0"
						: "opacity-0 max-h-0 translate-y-2 overflow-hidden"
				}`}
			>
				<Card className="">
					<CardContent className="space-y-4">
						<div className="flex items-center justify-between mb-4">
							<div className="flex items-center gap-3">
								<div className="p-2 rounded-lg bg-green-50">
									<Upload className="h-5 w-5 text-green-600" />
								</div>
								<div>
									<h3 className="text-lg font-semibold text-slate-900">
										CSV Upload
									</h3>
									<p className="text-sm text-slate-600">
										Mehrere Personen aus CSV-Datei importieren
									</p>
								</div>
							</div>
							{singleEmail && (
								<Button
									variant="ghost"
									size="sm"
									onClick={() => {
										setSingleEmail("");
										setEmailError(null);
									}}
									className="text-slate-500 hover:text-slate-700"
								>
									<X className="h-4 w-4 mr-1" />
									Email zurücksetzen
								</Button>
							)}
						</div>
						{/** biome-ignore lint/a11y/noStaticElementInteractions: <explanation> */}
						<div
							onDragEnter={handleDragEnter}
							onDragLeave={handleDragLeave}
							onDragOver={handleDragOver}
							onDrop={handleDrop}
							className={`relative border-2 border-dashed rounded-lg text-center transition-all duration-300 cursor-pointer ${
								hasData ? "p-4" : "p-8"
							} ${
								isDragOver
									? "border-blue-500 bg-blue-50"
									: "border-slate-300 hover:border-slate-400 hover:bg-slate-50"
							}`}
						>
							<input
								id="csv-file"
								type="file"
								accept=".csv,text/csv"
								onChange={handleFileChange}
								className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
							/>

							{hasData ? (
								/* Compact Mode */
								<div className="flex items-center justify-between">
									<div className="flex items-center gap-3">
										<div className="p-2 rounded-full bg-green-100">
											<CheckCircle2 className="h-4 w-4 text-green-600" />
										</div>
										<div className="text-left">
											<p className="text-sm font-medium text-slate-900">
												{file?.name || "CSV-Datei"}
											</p>
											<p className="text-xs text-slate-500">
												{validEmailsCount} gültige E-Mails gefunden
											</p>
										</div>
									</div>
									<Button
										onClick={(e) => {
											e.stopPropagation();
											clearBulkData();
										}}
										variant="ghost"
										size="sm"
										className="text-slate-500 hover:text-slate-700 z-20"
									>
										<X className="h-4 w-4" />
									</Button>
								</div>
							) : (
								/* Full Mode */
								<div className="flex flex-col items-center gap-3">
									<div
										className={`p-3 rounded-full ${isDragOver ? "bg-blue-100" : "bg-slate-100"}`}
									>
										<Upload
											className={`h-6 w-6 ${isDragOver ? "text-blue-600" : "text-slate-600"}`}
										/>
									</div>
									<div>
										<p className="text-sm font-medium text-slate-900">
											{isDragOver
												? "CSV-Datei hier ablegen"
												: "CSV-Datei hochladen"}
										</p>
										<p className="text-xs text-slate-500 mt-1">
											Datei hierher ziehen oder klicken zum Auswählen
										</p>
										<p className="text-xs text-slate-400 mt-1">
											Die erste Spalte sollte E-Mail-Adressen enthalten
										</p>
									</div>
								</div>
							)}
						</div>

						{/* Loading State */}
						{isLoading && (
							<div className="flex items-center justify-center gap-2 py-4">
								<div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
								<span className="text-sm text-slate-600">
									Analysiere CSV-Datei...
								</span>
							</div>
						)}

						{/* Error State */}
						{bulkError && (
							<div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
								<AlertCircle className="h-4 w-4 text-red-600" />
								<span className="text-sm text-red-700">{bulkError}</span>
							</div>
						)}

						{/* Preview Section */}
						{hasData && (
							<div className="space-y-4">
								<div className="flex items-center justify-between">
									<h4 className="text-sm font-medium flex items-center gap-2">
										<FileText className="h-4 w-4" />
										E-Mail-Vorschau
									</h4>
									<div className="flex gap-2">
										{validEmailsCount > 0 && (
											<Badge
												variant="outline"
												className="bg-green-50 text-green-700 border-green-200"
											>
												<CheckCircle2 className="h-3 w-3 mr-1" />
												{validEmailsCount} gültig
											</Badge>
										)}
										{invalidEmailsCount > 0 && (
											<Badge
												variant="outline"
												className="bg-red-50 text-red-700 border-red-200"
											>
												<X className="h-3 w-3 mr-1" />
												{invalidEmailsCount} ungültig
											</Badge>
										)}
									</div>
								</div>

								<div className="max-h-64 overflow-y-auto space-y-2 border  rounded-lg p-3">
									{parsedEmails.slice(0, 50).map((emailData, index) => (
										<div
											key={index}
											className={`flex items-center justify-between p-2 rounded-md text-sm ${
												emailData.isValid
													? "bg-green-50 border border-green-200"
													: "bg-red-50 border border-red-200"
											}`}
										>
											<span
												className={`font-medium ${emailData.isValid ? "text-green-900" : "text-red-900"}`}
											>
												{emailData.email}
											</span>
											<span className="text-xs text-slate-500">
												Zeile {emailData.row}
											</span>
										</div>
									))}
									{parsedEmails.length > 50 && (
										<div className="text-xs text-slate-500 text-center py-2 border-t ">
											... und {parsedEmails.length - 50} weitere E-Mails
										</div>
									)}
								</div>

								<div className="flex justify-end gap-3">
									<Button
										onClick={handleBulkImport}
										disabled={validEmailsCount === 0}
										className="flex items-center gap-2"
									>
										<Send className="h-4 w-4" />
										{validEmailsCount} Einladung(en) senden
									</Button>
								</div>
							</div>
						)}
					</CardContent>
				</Card>
			</div>
		</div>
	);
}
